import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { unSlugifyURL } from "@/app/handlers/urlFunctions.js";
import { ResumeDataService } from "@/app/services/create-resume/resume-data.js";

import { ApplicationDetails, getApplicationDetails, JobDetails } from "../utils/helpers";

interface UseJobDetailsReturn {
	jobId: string;
	jobDetails: JobDetails;
	setJobDetails: React.Dispatch<React.SetStateAction<JobDetails>>;
	loading: boolean;
	applicationDetails: ApplicationDetails;
	setApplicationDetails: React.Dispatch<React.SetStateAction<ApplicationDetails>>;
}

/**
 * Hook to manage job details fetching and state
 * @param data - Optional job data passed as prop
 * @returns Job details state and helpers
 */
export function useJobDetails(data?: { _id?: string }): UseJobDetailsReturn {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();

	const jobId = useMemo(() => {
		return unSlugifyURL(id) as string;
	}, [id]);

	const jobService = new ResumeDataService();
	const [jobDetails, setJobDetails] = useState<JobDetails>({});
	const [loading, setLoading] = useState(true);
	const [applicationDetails, setApplicationDetails] = useState<ApplicationDetails>({
		link: null,
		jobId: null,
		companyId: null,
		applicationType: "URL",
	});

	useEffect(() => {
		setLoading(true);
		jobService.getSelectedJob(
			data?._id || jobId,
			(response: { data: JobDetails }) => {
				setJobDetails(response.data);
				setApplicationDetails(getApplicationDetails(response.data));
				setLoading(false);
			},
			() => {
				setLoading(false);
				navigate("/jobs");
			},
		);
	}, [data, jobId]);

	return {
		jobId,
		jobDetails,
		setJobDetails,
		loading,
		applicationDetails,
		setApplicationDetails,
	};
}
